// input.js: pointer, look (pointer on desktop, device tilt on phones) and a
// safe wrapper around the sound score.
import { clamp, damp } from './util.js';

export class Pointer {
  constructor() {
    this.p = { x: 0.5, y: 0.5, dx: 0, dy: 0, down: false, inside: false };
    this.look = { x: 0, y: 0 };
    this.cx = innerWidth / 2; this.cy = innerHeight / 2; // CSS px, for the ring
    this.acc = { dx: 0, dy: 0 };
    this.last = null;
    this.mouse = false;
    this.gyro = null; // { x, y } -1..1 once DeviceOrientation delivers
    this.gyroBase = null;
    this.gyroAsked = false;
    // [gravity] the pointer (a finger on phones) has mass: where the lens sits (CSS px) and how strong it is.
    // Touch events keep firing while the page scrolls natively (touch pointer events get cancelled), so the
    // finger lens follows a scroll-drag.
    const L = this.lens = { x: innerWidth / 2, y: innerHeight / 2, k: 0, e: 0, moveT: -1e9, down: false, touch: false, travel: 0 };
    const lmove = (x, y) => { L.travel += Math.hypot(x - L.x, y - L.y); L.x = x; L.y = y; L.moveT = performance.now(); };
    addEventListener('pointermove', (e) => { if (e.pointerType !== 'touch') { L.touch = false; lmove(e.clientX, e.clientY); } }, { passive: true });
    addEventListener('pointerdown', (e) => { if (e.pointerType !== 'touch') lmove(e.clientX, e.clientY); }, { passive: true });
    const tl = (e) => {
      const t = e.touches && e.touches[0];
      if (!t) { L.down = false; return; }
      L.touch = true; L.down = true;
      if (e.type === 'touchstart') { L.x = t.clientX; L.y = t.clientY; }
      lmove(t.clientX, t.clientY);
    };
    for (const n of ['touchstart', 'touchmove', 'touchend', 'touchcancel']) addEventListener(n, tl, { passive: true });
    document.addEventListener('pointerleave', () => { L.moveT = -1e9; });
    const pos = (e) => [e.clientX / innerWidth, 1 - e.clientY / innerHeight];
    addEventListener('pointermove', (e) => {
      const [x, y] = pos(e);
      if (this.last) { this.acc.dx += x - this.last[0]; this.acc.dy += y - this.last[1]; }
      this.last = [x, y];
      this.p.x = x; this.p.y = y;
      this.cx = e.clientX; this.cy = e.clientY;
      this.mouse = e.pointerType === 'mouse';
      this.p.inside = this.mouse || this.p.down;
    }, { passive: true });
    addEventListener('pointerdown', (e) => {
      const [x, y] = pos(e);
      this.last = [x, y];
      this.p.x = x; this.p.y = y; this.p.down = true; this.p.inside = true;
      this.cx = e.clientX; this.cy = e.clientY;
      this.mouse = e.pointerType === 'mouse';
    }, { passive: true });
    const up = (e) => { this.p.down = false; if (e.pointerType !== 'mouse') { this.p.inside = false; this.last = null; } };
    addEventListener('pointerup', up, { passive: true });
    addEventListener('pointercancel', up, { passive: true });
    document.addEventListener('pointerleave', () => { this.p.inside = false; });
    addEventListener('blur', () => { this.p.inside = false; this.p.down = false; });
  }

  // DeviceOrientation: must be requested inside a user gesture on iOS 13+
  askGyro() {
    if (this.gyroAsked) return;
    this.gyroAsked = true;
    const listen = () => addEventListener('deviceorientation', (e) => this.onTilt(e), { passive: true });
    try {
      const DOE = window.DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === 'function') {
        DOE.requestPermission().then((s) => { if (s === 'granted') listen(); }).catch(() => { /* denied or not a gesture */ });
      } else if (DOE) listen();
    } catch (e) { /* unsupported */ }
  }

  onTilt(e) {
    if (e.beta === null || e.gamma === null) return;
    const landscape = (screen.orientation && /landscape/.test(screen.orientation.type)) || Math.abs(window.orientation || 0) === 90;
    let a = e.gamma, b = e.beta; // left-right, front-back
    if (landscape) { const s = (window.orientation || (screen.orientation && screen.orientation.angle) || 90) > 0 ? 1 : -1; a = b * s; b = -e.gamma * s; }
    if (!this.gyroBase) this.gyroBase = { a, b };
    // the rest position follows slowly, so any holding angle works
    this.gyroBase.a += (a - this.gyroBase.a) * 0.01;
    this.gyroBase.b += (b - this.gyroBase.b) * 0.01;
    this.gyro = { x: clamp((a - this.gyroBase.a) / 22, -1, 1), y: clamp(-(b - this.gyroBase.b) / 22, -1, 1) };
  }

  // [gravity] the mass eases in over 0.35 s while the pointer moves (or a finger is down) and out over
  // 0.8 s after 1.5 s idle or a release. QA: window.__lensForce = { x, y } (CSS px) pins it on.
  lensTick(dt, allow) {
    const L = this.lens, f = window.__lensForce;
    dt = Math.min(Math.max(dt || 0, 0), 0.1);
    if (f && Number.isFinite(f.x) && Number.isFinite(f.y)) { L.x = f.x; L.y = f.y; }
    const want = allow && (!!f || L.down || (!L.touch && performance.now() - L.moveT < 1500));
    L.k = want ? Math.min(1, L.k + dt / 0.35) : Math.max(0, L.k - dt / 0.8);
    L.e = L.k * L.k * (3 - 2 * L.k);
    return L;
  }

  tick(dt, forced) {
    this.p.dx = this.acc.dx; this.p.dy = this.acc.dy; this.acc.dx = 0; this.acc.dy = 0;
    let tx = 0, ty = 0;
    if (forced) { tx = forced[0]; ty = forced[1]; } else if (this.gyro && !this.mouse) { tx = this.gyro.x; ty = this.gyro.y; } else if (this.p.inside) { tx = (this.p.x - 0.5) * 2; ty = (this.p.y - 0.5) * 2; }
    const k = forced ? 60 : 3;
    this.look.x = damp(this.look.x, tx, k, dt);
    this.look.y = damp(this.look.y, ty, k, dt);
  }
}

// Every call into the sound module is guarded: a broken score must never
// break the film. After a few errors it goes quiet for good.
export class SafeScore {
  constructor(Ctor, opts) {
    this.s = null;
    this.errs = 0;
    try { this.s = new Ctor(opts); } catch (e) { console.warn('[v5] sound disabled:', e); }
  }
  call(fn, ...a) {
    if (!this.s || this.errs > 5) return undefined;
    try { const r = this.s[fn](...a); if (r && r.catch) r.catch((e) => this.err(fn, e)); return r; } catch (e) { this.err(fn, e); return undefined; }
  }
  err(fn, e) { this.errs++; console.warn(`[v5] sound.${fn} failed:`, e); }
  unlock() { return this.call('unlock'); }
  setMuted(m) { this.call('setMuted', m); }
  update(dt, st) { this.call('update', dt, st); }
  cue(n) { this.call('cue', n); }
  get running() { try { return !!(this.s && this.s.running); } catch (e) { return false; } }
}
